import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';
import { installationArtists } from './data';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

function getYouTubeEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}>
      <button
        className="absolute top-5 right-5 text-white/60 hover:text-white text-3xl leading-none"
        onClick={onClose}>
        ×
      </button>
      <img
        src={normalizeImageSrc(src)}
        alt="Lightbox"
        className="max-h-[90vh] max-w-full rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── LazyImage ─────────────────────────────────────────────────────────────────
function LazyImg({ src, alt, className, wrapperClass, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`relative overflow-hidden bg-gray-100 ${wrapperClass || ''}`}>
      <img
        src={normalizeImageSrc(src)}
        alt={alt}
        className={`transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} ${
          className || ''
        }`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
        onClick={onClick}
      />
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
      )}
    </div>
  );
}

// ── Detail page ───────────────────────────────────────────────────────────────
const KinstallationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const artist = installationArtists.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!artist) {
    return (
      <FullLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-400 mb-4">Artist not found.</p>
            <button
              onClick={() => navigate('/kinstallation')}
              className="text-primary-main font-bold text-sm hover:underline">
              ← Back to KiNSTALLATION
            </button>
          </div>
        </div>
      </FullLayout>
    );
  }

  const otherArtists = installationArtists.filter((a) => a.id !== id);

  return (
    <FullLayout>
      {/* ── Art backdrop strip ─────────────────────────────────────────── */}
      <div className="relative h-[260px] sm:h-[340px] lg:h-[400px] overflow-hidden">
        <img
          src={normalizeImageSrc(artist.project.images[1] || artist.project.images[0])}
          alt={artist.project.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Warm cream gradient from left to right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(108deg, #fcf2e9 0%, rgba(252,242,233,0.9) 35%, rgba(252,242,233,0.35) 65%, rgba(252,242,233,0) 100%)',
          }}
        />
        {/* Accent bar — bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{ backgroundColor: artist.accentColor }}
        />
        {/* Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="px-6 sm:px-10 lg:px-16 max-w-2xl">
            <p
              className="text-[10px] font-black uppercase tracking-[0.4em] mb-3"
              style={{ color: artist.accentColor }}>
              The Project
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[3rem] font-black text-kin-textdark leading-tight">
              {artist.project.name}
            </h2>
            {artist.project.subtitle && (
              <p className="text-kin-text text-sm mt-2 font-medium max-w-sm">
                {artist.project.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-14 lg:py-20">
        {/* ── Back button ──────────────────────────────────────────────────── */}
        <div className="mb-10">
          <button
            onClick={() => navigate('/kinstallation')}
            className="inline-flex items-center gap-2 text-sm font-bold text-kin-textdark hover:text-gray-900 transition-colors duration-150">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            KiNSTALLATION
          </button>
        </div>
        {/* ── Artist + Project layout ─────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.6fr] gap-12 lg:gap-16 items-start">
          {/* LEFT: Artist info */}
          <div>
            {/* Portrait */}
            <div
              className="relative rounded-2xl overflow-hidden mb-6"
              style={{ maxWidth: '360px' }}>
              <LazyImg
                src={artist.portrait}
                alt={artist.name}
                wrapperClass="aspect-square"
                className="w-full h-full object-cover"
              />
              {/* Accent bottom bar */}
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ backgroundColor: artist.accentColor }}
              />
            </div>

            {/* Name + role */}
            <p
              className="text-[10px] font-black uppercase tracking-widest mb-1"
              style={{ color: artist.accentColor }}>
              {artist.role}
            </p>
            <h2 className="text-2xl lg:text-3xl font-black text-gray-900 leading-tight">
              {artist.name}
            </h2>

            {/* Divider */}
            <div
              className="h-[2px] w-10 rounded-full my-4"
              style={{ backgroundColor: artist.accentColor }}
            />

            {/* Bio — single artist or collaborators */}
            {artist.collaborators ? (
              <div className="space-y-7">
                {artist.collaborators.map((c, i) => (
                  <div key={i}>
                    <p
                      className="text-[10px] font-black uppercase tracking-widest mb-2"
                      style={{ color: artist.accentColor }}>
                      {c.name}
                    </p>
                    <div className="space-y-2.5">
                      {c.bio.split('\n\n').map((para, j) => (
                        <p key={j} className="text-sm text-gray-500 leading-relaxed">
                          {para.trim()}
                        </p>
                      ))}
                    </div>
                    {c.instagram && (
                      <a
                        href={c.instagram}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 mt-3 text-xs font-bold transition-colors duration-150 hover:opacity-75"
                        style={{ color: artist.accentColor }}>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Follow {c.name.split(' ')[0]} on Instagram
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {artist.bio.split('\n\n').map((para, i) => (
                    <p key={i} className="text-sm text-gray-500 leading-relaxed">
                      {para.trim()}
                    </p>
                  ))}
                </div>

                {artist.instagram && (
                  <a
                    href={artist.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2.5 mt-6 px-5 py-3 rounded-full text-sm font-bold text-white transition-colors duration-200"
                    style={{ backgroundColor: artist.accentColor }}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    Follow on Instagram
                  </a>
                )}
              </>
            )}
          </div>

          {/* RIGHT: Project content */}
          <div>
            {/* Project heading */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="h-5 w-[3px] rounded-full"
                style={{ backgroundColor: artist.accentColor }}
              />
              <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
                The Project
              </span>
            </div>
            <h3 className="text-2xl lg:text-3xl font-black text-gray-900 leading-tight mb-2">
              {artist.project.name}
            </h3>
            {artist.project.subtitle && (
              <p className="text-sm font-bold text-gray-400 mb-5 uppercase tracking-wider">
                {artist.project.subtitle}
              </p>
            )}

            {/* Description */}
            <p className="text-base text-gray-600 leading-relaxed mb-8">
              {artist.project.description}
            </p>

            {/* ── Photo Gallery ── */}
            <div className="mb-8">
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400 mb-4">
                Artwork Gallery
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {artist.project.images.map((img, i) => (
                  <LazyImg
                    key={i}
                    src={img}
                    alt={`${artist.project.name} ${i + 1}`}
                    wrapperClass="aspect-square rounded-xl cursor-pointer ring-1 ring-gray-200 hover:ring-2 transition-all duration-200"
                    className="w-full h-full object-cover"
                    onClick={() => setLightboxSrc(img)}
                  />
                ))}
              </div>
              <p className="text-[10px] text-gray-300 mt-2">Tap an image to expand</p>
            </div>
          </div>
        </div>

        {/* ── Video — full width ────────────────────────────────────────── */}
        {artist.project.videoId && (
          <div className="mt-14 lg:mt-20">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-5 w-[3px] rounded-full"
                style={{ backgroundColor: artist.accentColor }}
              />
              <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
                Installation Video
              </span>
            </div>
            <div
              className="relative w-full overflow-hidden rounded-2xl bg-black shadow-xl"
              style={{ paddingBottom: '50%' }}>
              <iframe
                src={getYouTubeEmbedUrl(artist.project.videoId)}
                title={`${artist.project.name} – Installation Video`}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${artist.project.videoId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors duration-150">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              Watch on YouTube
            </a>
          </div>
        )}
      </div>

      {/* ── Other Artists ─────────────────────────────────────────────────── */}
      <section className="px-6 sm:px-10 lg:px-16 py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-5 w-[3px] rounded-full bg-primary-main" />
            <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
              Other Installations
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {otherArtists.map((a) => (
              <div
                key={a.id}
                onClick={() => navigate(`/kinstallation/${a.id}`)}
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 flex gap-0">
                {/* Image */}
                <div className="relative w-36 sm:w-44 flex-shrink-0 overflow-hidden">
                  <img
                    src={normalizeImageSrc(a.mainImage)}
                    alt={a.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ backgroundColor: a.accentColor }}
                  />
                </div>

                {/* Info */}
                <div className="p-4 flex flex-col justify-between flex-1 min-w-0">
                  <div>
                    <p
                      className="text-[9px] font-black uppercase tracking-widest mb-1"
                      style={{ color: a.accentColor }}>
                      {a.tag}
                    </p>
                    <h3 className="font-black text-gray-900 text-base leading-tight">{a.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{a.project.name}</p>
                  </div>
                  <div
                    className="inline-flex items-center gap-1 text-xs font-bold mt-3"
                    style={{ color: a.accentColor }}>
                    View Project
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <button
              onClick={() => navigate('/kinstallation')}
              className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors duration-150">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              Back to all installations
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </FullLayout>
  );
};

export default KinstallationDetail;
